import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Empty, Spinner, Field } from '../components/ui.jsx';
import { useConfirm, useToast } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import { IconPlus } from '../components/icons.jsx';

const TOOL_LABELS = {
  search_projects: 'Search projects',
  get_project: 'Read a project (full detail)',
  list_tasks: 'List tasks / overdue',
  list_contracts: 'List contracts / expiring',
  list_exclusivities: 'List exclusivities',
  list_companies: 'List companies',
  dashboard_summary: 'Dashboard summary',
  list_funds: 'List funds (bandi)',
  get_fund: 'Read a fund (full detail)',
  list_fund_clients: 'List fund clients',
  list_sal_due: 'List SAL milestones due',
  list_documents: 'List documents',
  read_document: 'Read document text (PDF/DOCX/TXT)',
  ocr_document: 'OCR scanned docs/images (vision)',
  fetch_url: 'Fetch web page text',
  create_task: 'Create tasks (write action)',
  update_task: 'Update tasks (write action)',
  create_project: 'Create projects (write action)',
  update_project: 'Update projects (write action)',
  create_company: 'Create companies (write action)',
  update_company: 'Update companies (write action)',
  create_contract: 'Create contracts (write action)',
  create_fund: 'Create funds / bandi (write action)',
  update_fund: 'Update funds (write action)',
  create_fund_client: 'Enroll fund clients (write action)',
  create_sal_milestone: 'Create SAL milestones (write action)',
};

const DOMAINS = [
  { key: 'project_type', label: 'Project types', hint: 'e.g. FIT-in, FIT-out, Supply only' },
  { key: 'company_specialty', label: 'Company specialties', hint: 'e.g. Lighting, Marble, Furniture' },
];
// labels/hints are translated at render time via t()

export default function Settings() {
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'manager';

  return (
    <div className="page fade-in">
      <h1 className="page-title">{t('Settings')}</h1>
      <p className="muted" style={{ marginTop: 4 }}>{t('Manage the classification categories used across companies and projects.')}</p>

      <div className="dash-cols" style={{ marginTop: 16 }}>
        {DOMAINS.map((d) => <CategoryPanel key={d.key} domain={d} canEdit={canEdit} isAdmin={user.role === 'admin'} />)}
      </div>

      {user.role === 'admin' && <TwoFactorPolicy />}
      {user.role === 'admin' && <AiSettings />}
    </div>
  );
}

const TWO_FA_ROLES = ['admin', 'manager', 'partner'];

function TwoFactorPolicy() {
  const toast = useToast();
  const [roles, setRoles] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/auth/2fa/policy').then((r) => setRoles(r.data.required_roles || [])).catch(() => setRoles([]));
  }, []);

  function toggle(role) {
    setRoles((rs) => (rs.includes(role) ? rs.filter((r) => r !== role) : [...rs, role]));
  }

  async function save() {
    setBusy(true);
    try {
      await api.put('/auth/2fa/policy', { required_roles: roles });
      toast(t('Two-factor policy saved'));
    } catch (e) { toast(e.message, { tone: 'error' }); }
    finally { setBusy(false); }
  }

  if (roles === null) return null;

  return (
    <section className="panel" style={{ marginTop: 18 }}>
      <h2 className="panel-title">{t('Two-factor authentication policy')}</h2>
      <p className="muted sm" style={{ marginTop: -6, marginBottom: 12 }}>
        {t('Users with these roles will be prompted to set up two-factor authentication on their Account page. This is a nudge, not a hard block — existing sessions are not interrupted.')}
      </p>
      <div className="tool-grid" style={{ marginBottom: 12 }}>
        {TWO_FA_ROLES.map((role) => (
          <label key={role} className={`ms-opt ${roles.includes(role) ? 'on' : ''}`}>
            <input type="checkbox" checked={roles.includes(role)} onChange={() => toggle(role)} />
            <span style={{ textTransform: 'capitalize' }}>{t(role)}</span>
          </label>
        ))}
      </div>
      <button className="btn primary sm" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
    </section>
  );
}

function AiSettings() {
  const [cfg, setCfg] = useState(null);
  const [keys, setKeys] = useState({ deepseek: '', xiaomi: '' });
  const [audioKey, setAudioKey] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() { setCfg((await api.get('/ai/config')).data); }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, []);

  if (err) return <section className="panel"><div className="alert error">{err}</div></section>;
  if (!cfg) return <section className="panel"><Spinner /></section>;

  const setProvider = (pid, k) => (e) =>
    setCfg({ ...cfg, providers: { ...cfg.providers, [pid]: { ...cfg.providers[pid], [k]: e.target.value } } });

  async function save() {
    setBusy(true); setMsg(''); setErr('');
    try {
      const payload = {
        active_provider: cfg.active_provider,
        autonomy: cfg.autonomy,
        vision_model: cfg.vision_model || '',
        audio: {
          base_url: cfg.audio?.base_url || '', stt_model: cfg.audio?.stt_model || '',
          tts_model: cfg.audio?.tts_model || '', tts_voice: cfg.audio?.tts_voice || '', api_key: audioKey,
        },
        system_prompt: cfg.system_prompt || '',
        tools: cfg.tools,
        providers: {
          deepseek: { base_url: cfg.providers.deepseek.base_url, model: cfg.providers.deepseek.model, api_key: keys.deepseek },
          xiaomi:   { base_url: cfg.providers.xiaomi.base_url,   model: cfg.providers.xiaomi.model,   api_key: keys.xiaomi },
        },
      };
      const res = await api.put('/ai/config', payload);
      setCfg(res.data);
      setKeys({ deepseek: '', xiaomi: '' });
      setAudioKey('');
      setMsg(t('AI settings saved.'));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <section className="panel" style={{ marginTop: 18 }}>
      <h2 className="panel-title">{t('AI assistant')}</h2>
      <p className="muted sm" style={{ marginTop: -6, marginBottom: 12 }}>
        {t('Keys are stored on the server and never sent back to the browser.')}
      </p>
      {msg && <div className="alert success">{msg}</div>}
      {err && <div className="alert error">{err}</div>}

      <Field label={t('Active provider')}>
        <select value={cfg.active_provider} onChange={(e) => setCfg({ ...cfg, active_provider: e.target.value })}>
          {Object.entries(cfg.providers).map(([pid, p]) => <option key={pid} value={pid}>{p.label}</option>)}
        </select>
      </Field>

      <Field label={t('Autonomy (how the assistant may act)')}>
        <select value={cfg.autonomy || 'approval'} onChange={(e) => setCfg({ ...cfg, autonomy: e.target.value })}>
          <option value="read_only">{t('Read-only — no changes, ever')}</option>
          <option value="approval">{t('Approval required — you confirm every action (recommended)')}</option>
          <option value="non_destructive">{t('Non-destructive auto — creates/updates run, deletes need approval')}</option>
          <option value="full">{t('Full access — runs everything automatically')}</option>
        </select>
      </Field>
      <p className="muted sm" style={{ marginTop: -6, marginBottom: 12 }}>
        {t('Write tools below must also be enabled. All AI changes are grouped into restore points you can undo.')}
      </p>

      {Object.entries(cfg.providers).map(([pid, p]) => (
        <div key={pid} className="provider-block">
          <h4>{p.label} {p.has_key ? <span className="badge green">{t('key set')}</span> : <span className="badge gray">{t('no key')}</span>}</h4>
          <div className="grid-2">
            <Field label={t('Base URL (OpenAI-compatible)')}><input value={p.base_url} onChange={setProvider(pid, 'base_url')} placeholder="https://…/v1" /></Field>
            <Field label={t('Model')}><input value={p.model} onChange={setProvider(pid, 'model')} placeholder="model-name" /></Field>
          </div>
          <Field label={`API key ${p.has_key ? t('(leave blank to keep)') : ''}`}>
            <input type="password" value={keys[pid]} onChange={(e) => setKeys({ ...keys, [pid]: e.target.value })} autoComplete="new-password" />
          </Field>
        </div>
      ))}

      <h4 style={{ marginBottom: 6 }}>{t('Multimodal & voice')}</h4>
      <Field label="Vision model (for image attachments — e.g. a MiMo-VL model; blank = active model)">
        <input value={cfg.vision_model || ''} onChange={(e) => setCfg({ ...cfg, vision_model: e.target.value })} placeholder="vision-model-name" />
      </Field>
      <div className="grid-2">
        <Field label="Audio base URL (OpenAI-compatible; blank = active provider)">
          <input value={cfg.audio?.base_url || ''} onChange={(e) => setCfg({ ...cfg, audio: { ...cfg.audio, base_url: e.target.value } })} placeholder="https://…/v1" />
        </Field>
        <Field label={`Audio API key ${cfg.audio?.has_key ? '(leave blank to keep)' : '(blank = provider key)'}`}>
          <input type="password" value={audioKey} onChange={(e) => setAudioKey(e.target.value)} autoComplete="new-password" />
        </Field>
      </div>
      <div className="grid-3">
        <Field label="Speech-to-text model"><input value={cfg.audio?.stt_model || ''} onChange={(e) => setCfg({ ...cfg, audio: { ...cfg.audio, stt_model: e.target.value } })} placeholder="asr model" /></Field>
        <Field label="Text-to-speech model"><input value={cfg.audio?.tts_model || ''} onChange={(e) => setCfg({ ...cfg, audio: { ...cfg.audio, tts_model: e.target.value } })} placeholder="tts model" /></Field>
        <Field label="TTS voice"><input value={cfg.audio?.tts_voice || ''} onChange={(e) => setCfg({ ...cfg, audio: { ...cfg.audio, tts_voice: e.target.value } })} placeholder="alloy" /></Field>
      </div>
      <p className="muted sm" style={{ marginTop: -4, marginBottom: 12 }}>
        Voice in (mic) and spoken replies use your Xiaomi plan’s ASR/TTS models via the OpenAI-style
        <code> /audio/transcriptions</code> and <code>/audio/speech</code> endpoints. Fill in the model names to enable.
      </p>

      <h4 style={{ marginBottom: 6 }}>{t('Authorized tools')}</h4>
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <button type="button" className="btn primary sm" onClick={() => {
          const all = Object.fromEntries(Object.keys(cfg.tools).map((k) => [k, true]));
          setCfg({ ...cfg, autonomy: 'full', tools: all });
        }}>{t('Grant full access (all tools + full autonomy)')}</button>
        <button type="button" className="btn ghost sm" onClick={() =>
          setCfg({ ...cfg, tools: Object.fromEntries(Object.keys(cfg.tools).map((k) => [k, true])) })}>{t('Enable all tools')}</button>
        <button type="button" className="btn ghost sm" onClick={() =>
          setCfg({ ...cfg, tools: Object.fromEntries(Object.keys(cfg.tools).map((k) => [k, false])) })}>{t('Disable all')}</button>
      </div>
      <p className="muted sm" style={{ marginTop: -4, marginBottom: 10 }}>
        {t('“Grant full access” turns on every tool and sets autonomy to Full — the AI will create, update and act automatically. Remember to click Save below. All changes remain undoable via restore points.')}
      </p>
      <div className="tool-grid">
        {Object.keys(cfg.tools).map((k) => (
          <label key={k} className={`ms-opt ${cfg.tools[k] ? 'on' : ''}`}>
            <input type="checkbox" checked={!!cfg.tools[k]}
              onChange={(e) => setCfg({ ...cfg, tools: { ...cfg.tools, [k]: e.target.checked } })} />
            <span>{t(TOOL_LABELS[k] || k)}</span>
          </label>
        ))}
      </div>

      <Field label={t('Extra system instructions (optional)')}>
        <textarea rows={3} value={cfg.system_prompt || ''} onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })} />
      </Field>

      <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save AI settings')}</button>
    </section>
  );
}

function CategoryPanel({ domain, canEdit, isAdmin }) {
  const confirm = useConfirm();
  const [items, setItems] = useState(null);
  const [name, setName] = useState('');
  const [err, setErr] = useState('');

  async function load() {
    setItems((await api.get(`/categories?domain=${domain.key}`)).data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function add(e) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) return;
    try {
      await api.post('/categories', { domain: domain.key, name: name.trim() });
      setName('');
      load();
    } catch (e2) { setErr(e2.message); }
  }

  async function remove(id) {
    if (!(await confirm({ title: t('Delete category'), message: t('Delete this category? It will be removed from any company/project using it.'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/categories/${id}`);
    load();
  }

  return (
    <section className="panel">
      <h2 className="panel-title">{t(domain.label)}</h2>
      <p className="muted sm" style={{ marginTop: -6, marginBottom: 10 }}>{t(domain.hint)}</p>

      {err && <div className="alert error">{err}</div>}

      {!items ? <Spinner /> : items.length === 0 ? <Empty>{t('None yet.')}</Empty> : (
        <ul className="line-list">
          {items.map((c) => (
            <li key={c.id}>
              <span className="ll-title">{c.name}</span>
              {isAdmin && <button className="btn ghost sm danger" onClick={() => remove(c.id)}>{t('Delete')}</button>}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <form onSubmit={add} className="inline-add">
          <input placeholder={t('Add new…')} value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn primary sm"><IconPlus width={14} height={14} /> {t('Add')}</button>
        </form>
      )}
    </section>
  );
}
